import React, { useState } from 'react';
import { RefreshCw, Loader2, Calendar, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { Membership } from './types';

interface RenewMembershipDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (billingPeriod: 'mensual' | 'trimestral' | 'anual') => Promise<void>;
  membership: Membership | null;
  memberName: string;
  isSubmitting: boolean;
}

const billingOptions = [
  { value: 'mensual', label: 'Mensual', duration: '1 mes', multiplier: 1 },
  { value: 'trimestral', label: 'Trimestral', duration: '3 meses', multiplier: 3 },
  { value: 'anual', label: 'Anual', duration: '12 meses', multiplier: 12, discount: '2 meses gratis' },
];

const planPrices = {
  bronce: 12000,
  plata: 27000,
  oro: 45000,
};

export const RenewMembershipDialog: React.FC<RenewMembershipDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  membership,
  memberName,
  isSubmitting,
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState<'mensual' | 'trimestral' | 'anual'>(
    membership?.billing_period || 'mensual'
  );

  const calculatePrice = () => {
    if (!membership) return 0;
    const basePrice = planPrices[membership.plan];
    const option = billingOptions.find(o => o.value === selectedPeriod);
    if (!option) return basePrice;
    
    if (selectedPeriod === 'anual') {
      // Annual = 10 months (2 free)
      return basePrice * 10;
    }
    return basePrice * option.multiplier;
  };

  const calculateNewEndDate = () => {
    const today = new Date();
    const option = billingOptions.find(o => o.value === selectedPeriod);
    if (!option) return today;
    
    const newDate = new Date(today);
    newDate.setMonth(newDate.getMonth() + option.multiplier);
    return newDate;
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleConfirm = async () => {
    await onConfirm(selectedPeriod);
    onClose();
  };

  if (!membership) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-blue-400" />
            Renovar Membresía
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Member info */}
          <div className="bg-white/5 rounded-xl p-3 border border-white/10">
            <p className="text-white/60 text-sm">Miembro</p>
            <p className="text-white font-medium">{memberName}</p>
          </div>

          {/* Current plan */}
          <div className="bg-gradient-to-r from-blue-500/20 to-blue-600/20 rounded-xl p-3 border border-blue-500/30">
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-blue-400" />
              <span className="text-white/60 text-sm">Plan actual</span>
            </div>
            <p className="text-white font-bold capitalize text-lg">{membership.plan}</p>
          </div>

          {/* Billing period selection */}
          <div>
            <Label className="text-white/70">Período de renovación</Label>
            <Select 
              value={selectedPeriod} 
              onValueChange={(v) => setSelectedPeriod(v as 'mensual' | 'trimestral' | 'anual')}
            >
              <SelectTrigger className="bg-white/10 border-white/20 text-white mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-white/20">
                {billingOptions.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    className="text-white hover:bg-white/10"
                  >
                    <div className="flex items-center justify-between w-full">
                      <span>{option.label} ({option.duration})</span>
                      {option.discount && (
                        <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full ml-2">
                          {option.discount}
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Price summary */}
          <div className="bg-white/5 rounded-xl p-4 border border-white/10 space-y-2">
            <div className="flex justify-between text-white/60">
              <span>Precio total</span>
              <span className="text-white font-bold text-lg">
                {formatPrice(calculatePrice())}
              </span>
            </div>
            <div className="flex justify-between text-white/60 text-sm">
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                Nueva fecha de vencimiento
              </span>
              <span className="text-white">
                {calculateNewEndDate().toLocaleDateString('es-CR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </span>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-white/20 text-white hover:bg-white/10"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isSubmitting}
            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Confirmar Renovación
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RenewMembershipDialog;
